import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/features/core/auth/context/AuthContext";
import {
  VIEW_TYPES as TYPES_VIEW_TYPES,
  OPERATIONS_ROLES as TYPES_OPERATIONS_ROLES,
  RESIDENT_ROLES as TYPES_RESIDENT_ROLES,
  CONTRACTOR_ROLES as TYPES_CONTRACTOR_ROLES,
  ALL_ROLES as TYPES_ALL_ROLES,
} from "@homy/types";

export const VIEW_TYPES = Object.freeze([...TYPES_VIEW_TYPES]);
export const OPERATIONS_ROLES = Object.freeze([...TYPES_OPERATIONS_ROLES]);
export const RESIDENT_ROLES = Object.freeze([...TYPES_RESIDENT_ROLES]);
export const CONTRACTOR_ROLES = Object.freeze([...TYPES_CONTRACTOR_ROLES]);
export const ALL_ROLES = Object.freeze(
  TYPES_ALL_ROLES.filter((r) => r !== "COLLABORATOR"),
);

const VIEW_STORAGE_KEY = "homy_view_preference";

/**
 * Computes available views based on symmetrical role pooling
 * @param {Object} user
 * @returns {string[]} Sorted array of unique available views
 */
export function computeAvailableViews(user) {
  if (!user) return ["resident"];

  const views = new Set();
  const allUserRoles = new Set();

  if (user.role) allUserRoles.add(user.role);
  if (Array.isArray(user.assignedRoles)) {
    user.assignedRoles.forEach((r) => allUserRoles.add(r));
  }
  if (Array.isArray(user.roles)) {
    user.roles.forEach((r) => allUserRoles.add(r));
  }
  if (Array.isArray(user.memberships)) {
    user.memberships.forEach((m) => {
      if (m.role) allUserRoles.add(m.role);
      if (Array.isArray(m.roles)) {
        m.roles.forEach((r) => allUserRoles.add(r));
      }
    });
  }

  for (const r of allUserRoles) {
    if (OPERATIONS_ROLES.includes(r)) views.add("operations");
    if (RESIDENT_ROLES.includes(r)) views.add("resident");
    if (CONTRACTOR_ROLES.includes(r)) views.add("contractor");
    if (r === "MAINTENANCE_COORDINATOR") views.add("contractor");
    if (r === "VIEWER") views.add("resident");
  }

  if (user.isTenant) views.add("resident");
  if (user.isVendor || user.vendorId) views.add("contractor");
  if (user.isPlatformAdmin || user.role === "SUPER_ADMIN" || user.role === "PLATFORM_ADMIN") views.add("operations");

  if (Array.isArray(user.tenancyContexts)) {
    user.tenancyContexts.forEach((ctx) => {
      if (Array.isArray(ctx.allowedViews)) {
        ctx.allowedViews.forEach((v) => {
          if (VIEW_TYPES.includes(v)) views.add(v);
        });
      }
      if (ctx.role) {
        if (OPERATIONS_ROLES.includes(ctx.role)) views.add("operations");
        if (RESIDENT_ROLES.includes(ctx.role)) views.add("resident");
        if (CONTRACTOR_ROLES.includes(ctx.role)) views.add("contractor");
      }
    });
  }

  if (views.size === 0) views.add("resident");

  return Array.from(views).sort();
}

/**
 * Resolves active view from deep-link pathname
 * @param {string} pathname
 * @returns {string|null}
 */
export function resolveViewFromRoute(pathname) {
  if (!pathname || typeof pathname !== "string") return null;

  if (
    pathname.startsWith("/vendor") ||
    pathname.startsWith("/contractor")
  ) {
    return "contractor";
  }

  if (
    pathname.startsWith("/tenant") ||
    pathname.startsWith("/resident") ||
    pathname === "/lease" ||
    pathname === "/pay"
  ) {
    return "resident";
  }

  if (
    pathname.startsWith("/properties") ||
    pathname.startsWith("/rentals") ||
    pathname.startsWith("/organizations") ||
    pathname.startsWith("/financials") ||
    pathname.startsWith("/pipelines") ||
    pathname.startsWith("/listings") ||
    pathname.startsWith("/databases") ||
    (pathname.startsWith("/applications") &&
      !pathname.startsWith("/public/applications"))
  ) {
    return "operations";
  }

  return null;
}

/**
 * Determines whether switching to target view requires an onboarding wizard
 * @param {string} targetView
 * @param {Object} user
 * @returns {string|null} Wizard identifier ("become_vendor" | "create_or_join_org") or null
 */
export function determineRequiredWizard(targetView, user) {
  if (!user) return "auth";

  if (targetView === "contractor") {
    const isVendor = Boolean(
      user.isVendor ||
        user.vendorId ||
        user.role === "VENDOR" ||
        (Array.isArray(user.assignedRoles) && user.assignedRoles.includes("VENDOR")) ||
        (Array.isArray(user.roles) && user.roles.includes("VENDOR"))
    );
    if (!isVendor) {
      return "become_vendor";
    }
  }

  if (targetView === "resident") {
    const isResident = Boolean(
      user.isTenant ||
        RESIDENT_ROLES.includes(user.role) ||
        (Array.isArray(user.assignedRoles) && user.assignedRoles.some((r) => RESIDENT_ROLES.includes(r))) ||
        (Array.isArray(user.roles) && user.roles.some((r) => RESIDENT_ROLES.includes(r)))
    );
    if (!isResident) {
      return "become_resident";
    }
  }

  if (targetView === "operations") {
    if (user.isPlatformAdmin || user.role === "PLATFORM_ADMIN" || user.role === "SUPER_ADMIN") {
      return null;
    }
    const hasOrg = Boolean(
      user.organizationId ||
        (Array.isArray(user.memberships) && user.memberships.length > 0)
    );
    const hasOpsRole = Boolean(
      OPERATIONS_ROLES.includes(user.role) ||
        (Array.isArray(user.assignedRoles) &&
          user.assignedRoles.some((r) => OPERATIONS_ROLES.includes(r))) ||
        (Array.isArray(user.roles) &&
          user.roles.some((r) => OPERATIONS_ROLES.includes(r)))
    );
    if (!hasOrg || !hasOpsRole) {
      return "create_or_join_org";
    }
  }

  return null;
}

export const ViewContext = createContext(null);

export function ViewProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const roleViews = useMemo(() => computeAvailableViews(user), [user]);

  const [unlockedViews, setUnlockedViews] = useState(() => {
    const set = new Set();
    try {
      const stored = localStorage.getItem("homy_unlocked_views");
      if (stored) {
        JSON.parse(stored).forEach((v) => {
          if (VIEW_TYPES.includes(v)) set.add(v);
        });
      }
    } catch (_) {}
    return set;
  });

  const availableViews = useMemo(() => {
    const set = new Set(roleViews);
    unlockedViews.forEach((v) => set.add(v));
    return Array.from(set).sort();
  }, [roleViews, unlockedViews]);

  // Symmetrical auto-selection: default to highest-privilege authorized view or persisted preference
  const [currentView, setCurrentView] = useState(() => {
    try {
      const stored = localStorage.getItem(VIEW_STORAGE_KEY);
      if (stored && VIEW_TYPES.includes(stored)) {
        return stored;
      }
    } catch (_) {}

    const fromRoute = resolveViewFromRoute(location?.pathname);
    if (fromRoute && VIEW_TYPES.includes(fromRoute)) {
      return fromRoute;
    }

    if (availableViews.includes("operations")) return "operations";
    if (availableViews.includes("contractor")) return "contractor";
    return availableViews[0] || "resident";
  });

  const [activeWizard, setActiveWizard] = useState(null);

  // Sync currentView if availableViews changes and currentView is no longer available
  useEffect(() => {
    if (!availableViews.includes(currentView)) {
      const stored = localStorage.getItem(VIEW_STORAGE_KEY);
      if (stored && availableViews.includes(stored)) {
        setCurrentView(stored);
      } else {
        const next =
          availableViews.includes("operations")
            ? "operations"
            : availableViews.includes("contractor")
              ? "contractor"
              : availableViews[0] || "resident";
        setCurrentView(next);
      }
    }
  }, [availableViews, currentView]);

  // Route auto-synchronization: deep-linking auto-selects matching view
  useEffect(() => {
    if (!location?.pathname) return;
    const routeView = resolveViewFromRoute(location.pathname);
    if (routeView && routeView !== currentView) {
      if (availableViews.includes(routeView)) {
        setCurrentView(routeView);
        try {
          localStorage.setItem(VIEW_STORAGE_KEY, routeView);
        } catch (_) {}
      }
    }
  }, [location?.pathname, currentView, availableViews]);

  const switchView = useCallback(
    (targetView, options = {}) => {
      if (!VIEW_TYPES.includes(targetView)) return;
      if (targetView === currentView) return;

      const isUnlocked = unlockedViews.has(targetView);
      const wizard = !isUnlocked ? determineRequiredWizard(targetView, user) : null;
      if (wizard && !options.skipWizard) {
        setActiveWizard(wizard);
        return wizard;
      }

      setCurrentView(targetView);
      try {
        localStorage.setItem(VIEW_STORAGE_KEY, targetView);
      } catch (_) {}

      // Cache purge on view/tenancy context transition to prevent data leakage
      try {
        queryClient.clear();
      } catch (_) {}

      // Route transition if needed
      if (!options.skipNavigation) {
        const currentPath = location?.pathname || "/";
        if (
          targetView === "contractor" &&
          (currentPath === "/vendor/cockpit" || currentPath.startsWith("/tenant") || currentPath.startsWith("/properties") || currentPath.startsWith("/rentals"))
        ) {
          navigate("/");
        } else if (
          targetView === "resident" &&
          (currentPath.startsWith("/vendor") ||
            currentPath.startsWith("/properties") ||
            currentPath.startsWith("/rentals"))
        ) {
          navigate("/");
        } else if (
          targetView === "operations" &&
          (currentPath.startsWith("/tenant") ||
            currentPath.startsWith("/vendor"))
        ) {
          navigate("/");
        }
      }
    },
    [currentView, user, unlockedViews, location?.pathname, navigate, queryClient]
  );

  const triggerWizard = useCallback((type) => {
    setActiveWizard(type);
  }, []);

  const closeWizard = useCallback(() => {
    setActiveWizard(null);
  }, []);

  const onCompleteWizard = useCallback(
    (wizardType, targetView = null) => {
      setActiveWizard(null);
      const viewToSelect =
        targetView ||
        (wizardType === "become_vendor" ? "contractor" : wizardType === "become_resident" ? "resident" : "operations");

      setUnlockedViews((prev) => {
        const next = new Set(prev);
        next.add(viewToSelect);
        try {
          localStorage.setItem(
            "homy_unlocked_views",
            JSON.stringify(Array.from(next))
          );
        } catch (_) {}
        return next;
      });

      setCurrentView(viewToSelect);
      try {
        localStorage.setItem(VIEW_STORAGE_KEY, viewToSelect);
        queryClient.clear();
      } catch (_) {}

      if (viewToSelect === "contractor") {
        navigate("/");
      } else if (viewToSelect === "resident") {
        navigate("/");
      } else if (viewToSelect === "operations") {
        navigate("/");
      }
    },
    [navigate, queryClient]
  );

  const value = useMemo(
    () => ({
      currentView,
      activeView: currentView,
      availableViews,
      switchView,
      setActiveView: switchView,
      activeWizard,
      triggerWizard,
      closeWizard,
      onCompleteWizard,
      isOperations: currentView === "operations",
      isResident: currentView === "resident",
      isContractor: currentView === "contractor",
    }),
    [
      currentView,
      availableViews,
      switchView,
      activeWizard,
      triggerWizard,
      closeWizard,
      onCompleteWizard,
    ]
  );

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
}

ViewProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useView() {
  const context = useContext(ViewContext);
  if (!context) {
    // Graceful fallback for isolated components or unit tests without provider
    return {
      currentView: "operations",
      activeView: "operations",
      availableViews: ["operations", "resident", "contractor"],
      switchView: () => {},
      setActiveView: () => {},
      activeWizard: null,
      triggerWizard: () => {},
      closeWizard: () => {},
      onCompleteWizard: () => {},
      isOperations: true,
      isResident: false,
      isContractor: false,
    };
  }
  return context;
}

export function useCurrentView() {
  return useView();
}

export default ViewContext;
